import {
  tableMemo,
  type Row,
  type RowData,
  type RowModel,
  type Table,
  type Table_Internal,
  type TableFeatures,
} from '@tanstack/react-table';

import { DATA_TABLE_ROW_EXPANDED_ID_SUFFIX } from '../../constants.js';

/**
 * Row model which reflects the order given by the rowOrder set.
 * @returns new row model reflecting the order.
 * @internal
 */
export function createRowOrderRowModel<
  TFeatures extends TableFeatures,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TData extends RowData = any,
>(): (table: Table<TFeatures, TData>) => () => RowModel<TFeatures, TData> {
  return (_table) => {
    const table = _table as Table_Internal<TFeatures, TData>;
    return tableMemo({
      feature: 'DataTableRowOrderFeature',
      table,
      fnName: 'table.getRowOrderRowModel',
      memoDeps: () => [
        table.getPreRowOrderRowModel(),
        table.options.enableRowOrdering,
        table.atoms.rowOrder?.get(),
        table.atoms.sorting?.get(),
      ],
      fn: (preRowModel) => _createRowOrderRowModel(table),
    });
  };
}

function _createRowOrderRowModel<
  TFeatures extends TableFeatures,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TData extends RowData = any,
>(table: Table_Internal<TFeatures, TData>) {
  const preRowOrderRowModel = table.getPreRowOrderRowModel();
  const rowOrder = table.atoms.rowOrder?.get();
  const storingState = table.atoms.sorting?.get();

  // only order if rowOrder is enabled and we are not currently sorting
  if (
    table.options.enableRowOrdering !== true ||
    (storingState && storingState.length !== 0)
  ) {
    return preRowOrderRowModel;
  }

  const { flatRows, rows, rowsById } = preRowOrderRowModel;

  if (rowOrder === undefined) {
    table.setRowOrder(flatRows.map((row) => row.id));
    return preRowOrderRowModel;
  }

  const orderRows = (rows: Row<TFeatures, TData>[]) => {
    const orderedData = [...rows];

    orderedData.sort((rowA, rowB) => rowOrderCompareFn(rowOrder, rowA, rowB));

    // If there are sub-rows, also order them
    orderedData.forEach((row) => {
      if (row.subRows?.length) {
        row.subRows = orderRows(row.subRows);
      }
    });

    return orderedData;
  };

  const newRowsById = Object.keys(rowsById)
    .sort((a, b) => rowOrderCompareStrFn(rowOrder, a, b))
    .reduce((obj: Record<string, Row<TFeatures, TData>>, key: string) => {
      obj[key] = rowsById[key];
      return obj;
    }, {});

  return {
    flatRows: orderRows(flatRows),
    rows: orderRows(rows),
    rowsById: newRowsById,
  };
}

/**
 * Compares when a and b are objects.
 * @param rowOrder - set of ids.
 * @param a - object containing id 1
 * @param b - object containing id 2
 * @returns negative, zero or positive value as required for sort function.
 */
function rowOrderCompareFn(
  rowOrder: string[],
  a: { id: string },
  b: { id: string },
): number {
  let indexA = rowOrder.indexOf(a.id);
  if (indexA === -1) {
    indexA = rowOrder.indexOf(`${a.id}${DATA_TABLE_ROW_EXPANDED_ID_SUFFIX}`);
  }

  let indexB = rowOrder.indexOf(b.id);
  if (indexA === -1) {
    indexB = rowOrder.indexOf(`${b.id}${DATA_TABLE_ROW_EXPANDED_ID_SUFFIX}`);
  }
  return indexA - indexB;
}

/**
 * Compares when a and b are strings.
 * @param rowOrder - set of ids.
 * @param a - id 1
 * @param b - id 2
 * @returns negative, zero or positive value as required for sort function.
 */
function rowOrderCompareStrFn(
  rowOrder: string[],
  a: string,
  b: string,
): number {
  let indexA = rowOrder.indexOf(a);
  if (indexA === -1) {
    indexA = rowOrder.indexOf(`${a}${DATA_TABLE_ROW_EXPANDED_ID_SUFFIX}`);
  }

  let indexB = rowOrder.indexOf(b);
  if (indexA === -1) {
    indexB = rowOrder.indexOf(`${b}${DATA_TABLE_ROW_EXPANDED_ID_SUFFIX}`);
  }
  return indexA - indexB;
}
